import { Icon, Tooltip } from '@aloft/toolkit-sui';

import { KeyboardEvent, useRef, useState } from 'react';
import { Controller, useWatch } from 'react-hook-form';

import { useZodFormContext } from '../../../../hooks/useZodForm.ts';
import { RecommendCompsFormSchema } from '../../types.ts';
import { FilterSection } from './FilterSection.tsx';

import './KeywordSearchFilter.scss';

const TOOLTIP_LABEL = 'Press Enter to add a keyword. You can add multiple.';
const MAX_KEYWORD_LENGTH = 30;

export const KeywordSearchFilter = () => {
  const { control, setValue } = useZodFormContext<RecommendCompsFormSchema>();
  const keywords = useWatch({ control, name: 'search.filters.keywords' }) ?? [];
  const [draft, setDraft] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const showClear = keywords.length > 0;
  const showTooltip = isFocused && draft.length === 0 && keywords.length === 0;

  const commitKeyword = (raw: string, current: string[], onChange: (next: string[]) => void) => {
    const next = raw.trim().slice(0, MAX_KEYWORD_LENGTH);
    if (!next) return;
    const alreadyExists = current.some((keyword) => keyword.toLowerCase() === next.toLowerCase());
    if (alreadyExists) {
      setDraft('');
      return;
    }
    onChange([...current, next]);
    setDraft('');
  };

  return (
    <FilterSection
      label="Keyword search"
      showClear={showClear}
      onClear={() => setValue('search.filters.keywords', [], { shouldDirty: true })}
    >
      <Controller
        name="search.filters.keywords"
        control={control}
        render={({ field }) => (
          <Tooltip label={TOOLTIP_LABEL} isOpen={showTooltip} placement="top" hasArrow fullWidth>
            <div
              className={`keyword-search-input${isFocused ? ' focused' : ''}`}
              onClick={() => inputRef.current?.focus()}
            >
              {field.value?.map((keyword) => (
                <span key={keyword} className="keyword-pill">
                  <button
                    type="button"
                    className="keyword-pill-remove"
                    aria-label={`Remove ${keyword}`}
                    onClick={(event) => {
                      event.stopPropagation();
                      field.onChange((field.value ?? []).filter((item) => item !== keyword));
                    }}
                  >
                    <Icon icon="close" size="md" color="buttonDefault" />
                  </button>
                  <span className="keyword-pill-label">{keyword}</span>
                </span>
              ))}
              <input
                ref={inputRef}
                className="keyword-draft-input"
                value={draft}
                maxLength={MAX_KEYWORD_LENGTH}
                placeholder={(field.value?.length ?? 0) > 0 ? undefined : 'Search'}
                onChange={(event) => setDraft(event.target.value.slice(0, MAX_KEYWORD_LENGTH))}
                onFocus={() => setIsFocused(true)}
                onBlur={() => setIsFocused(false)}
                onKeyDown={(event: KeyboardEvent<HTMLInputElement>) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    commitKeyword(draft, field.value ?? [], field.onChange);
                    return;
                  }
                  if (
                    event.key === 'Backspace' &&
                    draft.length === 0 &&
                    (field.value?.length ?? 0) > 0
                  ) {
                    event.preventDefault();
                    field.onChange((field.value ?? []).slice(0, -1));
                  }
                }}
              />
            </div>
          </Tooltip>
        )}
      />
    </FilterSection>
  );
};
